<script setup lang="ts">
import axios from 'axios'
import { notifyError, fullLogOTT, getDevicesFromTree, getAssetsFromTree, msToTime } from '@/utils/helpers'
import type { OmniTBObjectTree } from '@/types/omniTBCustomer'
import type { ECOption } from '@/plugins/echarts'

definePage({
  meta: {
    id: 'ott-temp',
    title: 'Températures',
    requiresAuth: false,
    disableLayout: true
  },
})

const apiOmniAnon = axios.create({
  baseURL: import.meta.env.VITE_OMNI_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

let apiConfig: { headers: Record<string, string> } | null = null
const isLoading = ref(true)

declare interface TableTelemetry {
  id: string
  name: string
  label: string
  type: 'LSE01' | 'S5' | 'S31B-LB' | 'TypeK-New' | 'TypeK' | 'MT11'
  temperature: number
  water: number | null
  last_ts: number
}

const viewMode = ref<'devices' | 'assets'>(new URLSearchParams(window.location.search).has('assets') ? 'assets' : 'devices')
const devices = ref<OmniTBObjectTree[]>([])
const assetNodes = ref<OmniTBObjectTree[]>([])
const assets = ref<{ id: string; label: string }[]>([])
const telemetries = ref<TableTelemetry[]>([])
const selectedIds = ref<string[]>([])
const assetAggType = ref<'AVG' | 'MIN' | 'MAX'>('AVG')

watch(viewMode, () => { selectedIds.value = []; charts.value = []; assetCharts.value = [] })

const today = new Date()
const thirtyOneDaysAgo = new Date(today)
thirtyOneDaysAgo.setDate(today.getDate() - 31)

const startDate = ref<string>(thirtyOneDaysAgo.toISOString().slice(0, 10))
const endDate = ref<string>(today.toISOString().slice(0, 10))
const aggEnabled = ref(true)
const aggHours = ref(24)
const noRH = new URLSearchParams(window.location.search).has('norh')
const isFetching = ref(false)

declare interface DeviceTableRow {
  ts: number
  date: string
  temp: number | null
  tempMin: number | null
  tempMax: number | null
  water: number | null
  waterMin: number | null
  waterMax: number | null
}

declare interface DeviceChartData {
  name: string
  hasWater: boolean
  option: ECOption
  tableData: DeviceTableRow[]
}

declare interface AssetTableRow {
  ts: number
  date: string
  [deviceKey: string]: number | string | null
}

declare interface AssetChartData {
  name: string
  option: ECOption
  tableData: AssetTableRow[]
  tableHeaders: { title: string; key: string }[]
}

const charts = ref<DeviceChartData[]>([])
const assetCharts = ref<AssetChartData[]>([])

function getTempKey(type: TableTelemetry['type']) {
  switch (type) {
    case 'S5': return 'data_temperature'
    case 'S31B-LB': return 'data_TempC_SHT'
    case 'TypeK-New': case 'TypeK': return 'data_Temperature'
    default: return 'data_temp_SOIL'
  }
}

function getWaterKey(type: TableTelemetry['type']): string | null {
  switch (type) {
    case 'S5': return 'data_humidity'
    case 'S31B-LB': return 'data_Hum_SHT'
    case 'TypeK-New': case 'TypeK': return null
    default: return 'data_water_SOIL'
  }
}

onMounted(async () => {
  isLoading.value = true

  const log = await fullLogOTT(apiOmniAnon, window, import.meta.env.VITE_OMNI_TEST_CLIENTID)
  if (log == null || log.error != null || log.customer == null || log.customer.objectTree == null) {
    notifyError(log?.error ?? 'Unknown error during OTT login')
    isLoading.value = false
    return
  }

  apiConfig = { headers: { 'X-APIKey': log.apiKey, 'X-ThingsBoard': log.tbToken } }

  const allTypes = ['LSE01', 'S5', 'S31B-LB', 'TypeK-New', 'TypeK', 'MT11'] as const
  const typesParam = new URLSearchParams(window.location.search).get('types')
  const activeTypes = typesParam
    ? typesParam.split(',').filter((t): t is typeof allTypes[number] => (allTypes as readonly string[]).includes(t))
    : [...allTypes]

  const devicesByType: Record<string, OmniTBObjectTree[]> = {}
  for (const t of activeTypes) {
    devicesByType[t] = getDevicesFromTree(log.customer.objectTree, t)
  }
  devices.value = activeTypes.flatMap(t => devicesByType[t])

  const deviceTypes = new Map<string, typeof allTypes[number]>()
  for (const t of activeTypes) {
    for (const d of devicesByType[t]) deviceTypes.set(d.id.id, t)
  }

  try {
    for (const device of devices.value) {
      const tele = await apiOmniAnon.get('/api/tb/telemetry/DEVICE/' + device.id.id + '/values/latestTimeseries', apiConfig)
      if (tele.data) {
        const dType = deviceTypes.get(device.id.id)!
        const tempKey = getTempKey(dType)
        const waterKey = getWaterKey(dType)
        const tel: TableTelemetry = {
          id: device.id.id,
          name: device.name,
          label: device.label,
          type: dType,
          temperature: tele.data[tempKey]?.[0]?.value || 0,
          water: waterKey ? (tele.data[waterKey]?.[0]?.value || 0) : null,
          last_ts: tele.data[tempKey]?.[0]?.ts || (waterKey ? tele.data[waterKey]?.[0]?.ts : 0) || 0
        }

        telemetries.value.push(tel)
      }
    }

    //console.log('Telemetry:', telemetries.value)
  } catch (error) {
    notifyError('Error fetching telemetry: ' + error)
  }

  // Load assets
  const assetTypesParam = new URLSearchParams(window.location.search).get('assetTypes')
  const assetTypes = assetTypesParam ? assetTypesParam.split(',') : [null]
  const allAssets = assetTypes.flatMap(t => getAssetsFromTree(log.customer!.objectTree, t))
  assetNodes.value = allAssets
  assets.value = allAssets.map(a => ({ id: a.id.id, label: a.label || a.name }))

  isLoading.value = false
})

async function fetchData() {
  if (selectedIds.value.length === 0) {
    notifyError('Veuillez sélectionner au moins un capteur')
    return
  }

  isFetching.value = true
  charts.value = []

  const startTs = new Date(startDate.value).getTime()
  const endTs = new Date(endDate.value).getTime() + 86400000 // end of day

  const intervalMs = aggEnabled.value ? aggHours.value * 3600000 : 0
  const aggs = aggEnabled.value ? ['AVG', 'MIN', 'MAX'] as const : [null] as const

  try {
    for (const id of selectedIds.value) {
      const device = telemetries.value.find(t => t.id === id)
      if (!device) continue

      const tempKey = getTempKey(device.type)
      const waterKey = getWaterKey(device.type)
      const hasWater = waterKey != null
      const keys = hasWater ? `${tempKey},${waterKey}` : tempKey

      const results: Record<string, { ts: number; value: number }[]> = {}

      for (const agg of aggs) {
        const aggParams = agg ? `&interval=${intervalMs}&agg=${agg}` : ''
        const url = `/api/tb/telemetry/DEVICE/${id}/values/timeseries` +
          `?keys=${keys}&startTs=${startTs}&endTs=${endTs}&limit=100000${aggParams}`

        const res = await apiOmniAnon.get(url, apiConfig!)

        const suffix = agg ? `_${agg}` : ''
        const keysToProcess = hasWater ? [tempKey, waterKey!] : [tempKey]
        for (const key of keysToProcess) {
          results[`${key}${suffix}`] = (res.data[key] || [])
            .map((p: { ts: number; value: string }) => ({ ts: p.ts, value: parseFloat(p.value) }))
            .sort((a: { ts: number }, b: { ts: number }) => a.ts - b.ts)
        }
      }

      const tempSeries: ECOption['series'] = aggEnabled.value
        ? [
            { name: 'Temp moy.', type: 'line', showSymbol: false, yAxisIndex: 0, data: results[`${tempKey}_AVG`].map(p => [p.ts, p.value]) },
            { name: 'Temp min.', type: 'line', showSymbol: false, yAxisIndex: 0, lineStyle: { type: 'dashed', width: 1 }, data: results[`${tempKey}_MIN`].map(p => [p.ts, p.value]) },
            { name: 'Temp max.', type: 'line', showSymbol: false, yAxisIndex: 0, lineStyle: { type: 'dashed', width: 1 }, data: results[`${tempKey}_MAX`].map(p => [p.ts, p.value]) },
          ]
        : [
            { name: 'Température', type: 'line', showSymbol: false, yAxisIndex: 0, data: results[tempKey].map(p => [p.ts, p.value]) },
          ]

      const waterSeries: ECOption['series'] = (noRH || !hasWater) ? [] : (aggEnabled.value
        ? [
            { name: 'Humidité moy.', type: 'line', showSymbol: false, yAxisIndex: 1, data: results[`${waterKey}_AVG`].map(p => [p.ts, p.value]) },
            { name: 'Humidité min.', type: 'line', showSymbol: false, yAxisIndex: 1, lineStyle: { type: 'dashed', width: 1 }, data: results[`${waterKey}_MIN`].map(p => [p.ts, p.value]) },
            { name: 'Humidité max.', type: 'line', showSymbol: false, yAxisIndex: 1, lineStyle: { type: 'dashed', width: 1 }, data: results[`${waterKey}_MAX`].map(p => [p.ts, p.value]) },
          ]
        : [
            { name: 'Humidité', type: 'line', showSymbol: false, yAxisIndex: 1, data: results[waterKey].map(p => [p.ts, p.value]) },
          ])

      // Build table data by merging temp and water on timestamp
      const tsMap = new Map<number, DeviceTableRow>()
      const tblTempKey = aggEnabled.value ? `${tempKey}_AVG` : tempKey
      const tblWaterKey = hasWater ? (aggEnabled.value ? `${waterKey}_AVG` : waterKey) : null
      for (const p of results[tblTempKey] || []) {
        tsMap.set(p.ts, { ts: p.ts, date: new Date(p.ts).toLocaleString(), temp: p.value, tempMin: null, tempMax: null, water: null, waterMin: null, waterMax: null })
      }
      if (tblWaterKey) {
        for (const p of results[tblWaterKey] || []) {
          const existing = tsMap.get(p.ts)
          if (existing) existing.water = p.value
          else tsMap.set(p.ts, { ts: p.ts, date: new Date(p.ts).toLocaleString(), temp: null, tempMin: null, tempMax: null, water: p.value, waterMin: null, waterMax: null })
        }
      }
      if (aggEnabled.value) {
        for (const p of results[`${tempKey}_MIN`] || []) {
          const row = tsMap.get(p.ts)
          if (row) row.tempMin = p.value
        }
        for (const p of results[`${tempKey}_MAX`] || []) {
          const row = tsMap.get(p.ts)
          if (row) row.tempMax = p.value
        }
        if (hasWater) {
          for (const p of results[`${waterKey}_MIN`] || []) {
            const row = tsMap.get(p.ts)
            if (row) row.waterMin = p.value
          }
          for (const p of results[`${waterKey}_MAX`] || []) {
            const row = tsMap.get(p.ts)
            if (row) row.waterMax = p.value
          }
        }
      }
      const tableData = Array.from(tsMap.values()).sort((a, b) => a.ts - b.ts)

      charts.value.push({
        name: device.name,
        hasWater: hasWater && !noRH,
        tableData,
        option: {
          backgroundColor: 'transparent',
          tooltip: { trigger: 'axis' },
          legend: {},
          grid: { top: 40, left: '3%', right: '3%', bottom: '3%', containLabel: true },
          xAxis: {
            type: 'time',
            axisLabel: { rotate: 30 },
          },
          yAxis: (noRH || !hasWater)
            ? [{ type: 'value', name: '°C', position: 'left' }]
            : [
                { type: 'value', name: '°C', position: 'left' },
                { type: 'value', name: '%', position: 'right' },
              ],
          series: [...(tempSeries as any[]), ...(waterSeries as any[])],
        },
      })
    }
  } catch (error) {
    notifyError('Erreur lors de la récupération des données: ' + error)
  }

  isFetching.value = false
}

async function fetchAssetData() {
  if (selectedIds.value.length === 0) {
    notifyError('Veuillez sélectionner au moins un actif')
    return
  }

  isFetching.value = true
  assetCharts.value = []

  const startTs = new Date(startDate.value).getTime()
  const endTs = new Date(endDate.value).getTime() + 86400000
  const intervalMs = aggEnabled.value ? aggHours.value * 3600000 : 0
  const agg = aggEnabled.value ? assetAggType.value : null

  try {
    for (const assetId of selectedIds.value) {
      const assetNode = assetNodes.value.find(a => a.id.id === assetId)
      if (!assetNode) continue

      const assetDevices = getDevicesFromTree(assetNode, null)
      if (assetDevices.length === 0) continue

      const assetName = assetNode.label || assetNode.name
      const allSeries: any[] = []
      const tsMap = new Map<number, AssetTableRow>()
      const tableHeaders: { title: string; key: string }[] = [{ title: 'Date', key: 'date' }]

      for (const dev of assetDevices) {
        const devType = dev.type as TableTelemetry['type']
        const tempKey = getTempKey(devType)
        const aggParams = agg ? `&interval=${intervalMs}&agg=${agg}` : ''
        const url = `/api/tb/telemetry/DEVICE/${dev.id.id}/values/timeseries` +
          `?keys=${tempKey}&startTs=${startTs}&endTs=${endTs}&limit=100000${aggParams}`

        const res = await apiOmniAnon.get(url, apiConfig!)
        const points = (res.data[tempKey] || [])
          .map((p: { ts: number; value: string }) => ({ ts: p.ts, value: parseFloat(p.value) }))
          .sort((a: { ts: number }, b: { ts: number }) => a.ts - b.ts)

        const devLabel = dev.label || dev.name
        const colKey = `dev_${dev.id.id}`
        tableHeaders.push({ title: `${devLabel} (°C)`, key: colKey })

        allSeries.push({
          name: devLabel,
          type: 'line',
          showSymbol: false,
          data: points.map((p: { ts: number; value: number }) => [p.ts, p.value]),
        })

        for (const p of points) {
          const val = Math.round(p.value * 100) / 100
          const existing = tsMap.get(p.ts)
          if (existing) {
            existing[colKey] = val
          } else {
            const row: AssetTableRow = { ts: p.ts, date: new Date(p.ts).toLocaleString() }
            row[colKey] = val
            tsMap.set(p.ts, row)
          }
        }
      }

      const tableData = Array.from(tsMap.values()).sort((a, b) => a.ts - b.ts)

      assetCharts.value.push({
        name: assetName,
        tableHeaders,
        tableData,
        option: {
          backgroundColor: 'transparent',
          tooltip: { trigger: 'axis' },
          legend: {},
          grid: { top: 40, left: '3%', right: '3%', bottom: '3%', containLabel: true },
          xAxis: { type: 'time', axisLabel: { rotate: 30 } },
          yAxis: [{ type: 'value', name: '°C', position: 'left' }],
          series: allSeries,
        },
      })
    }
  } catch (error) {
    notifyError('Erreur lors de la récupération des données: ' + error)
  }

  isFetching.value = false
}

function exportAssetCsv(chart: AssetChartData) {
  const headerRow = chart.tableHeaders.map(h => h.title)
  const keys = chart.tableHeaders.map(h => h.key)
  const rows = chart.tableData.map(r =>
    keys.map(k => k === 'date' ? `"${r[k]}"` : (r[k] != null ? Number(r[k]).toFixed(2) : ''))
  )
  const csv = '\uFEFF' + [headerRow, ...rows].map(r => r.join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${chart.name}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function exportCsv(chart: DeviceChartData) {
  const isAgg = chart.tableData.length > 0 && chart.tableData[0].tempMin != null
  const showWater = chart.hasWater
  const headerRow = isAgg
    ? showWater
      ? ['Date', 'Temp moy. (°C)', 'Temp min. (°C)', 'Temp max. (°C)', 'Humid. moy. (%)', 'Humid. min. (%)', 'Humid. max. (%)']
      : ['Date', 'Temp moy. (°C)', 'Temp min. (°C)', 'Temp max. (°C)']
    : showWater
      ? ['Date', 'Temp (°C)', 'Humidité (%)']
      : ['Date', 'Temp (°C)']
  const rows = chart.tableData.map(r => isAgg
    ? showWater
      ? [`"${r.date}"`, r.temp?.toFixed(2) ?? '', r.tempMin?.toFixed(2) ?? '', r.tempMax?.toFixed(2) ?? '', r.water?.toFixed(2) ?? '', r.waterMin?.toFixed(2) ?? '', r.waterMax?.toFixed(2) ?? '']
      : [`"${r.date}"`, r.temp?.toFixed(2) ?? '', r.tempMin?.toFixed(2) ?? '', r.tempMax?.toFixed(2) ?? '']
    : showWater
      ? [`"${r.date}"`, r.temp?.toFixed(2) ?? '', r.water?.toFixed(2) ?? '']
      : [`"${r.date}"`, r.temp?.toFixed(2) ?? '']
  )
  const csv = '\uFEFF' + [headerRow, ...rows].map(r => r.join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${chart.name}.csv`
  a.click()
  URL.revokeObjectURL(url)
}
</script>

<template>
  <v-row class="ma-1">
    <v-col cols="12">
      <v-radio-group v-model="viewMode" inline hide-details>
        <v-radio label="Appareils" value="devices" />
        <v-radio label="Actifs" value="assets" />
      </v-radio-group>
    </v-col>
  </v-row>
  <v-row class="ma-1">
    <v-col cols="7">
      <v-data-table
        v-if="viewMode === 'devices'"
        v-model="selectedIds" :items="telemetries" item-value="id" show-select
        :headers="[
          { title: 'Nom', key: 'label' },
          { title: 'Température', key: 'temperature' },
          ...(noRH ? [] : [{ title: 'Humidité', key: 'water' }]),
          { title: 'Dernière donnée', key: 'last_ts' }
        ]"
        :sort-by="[{ key: 'label', order: 'asc' }]" class="elevation-1" :loading="isLoading">
        <template #item.temperature="{ item }">
          {{ item.temperature.toFixed(2) }} °C
        </template>
        <template #item.water="{ item }">
          {{ item.water != null ? item.water.toFixed(2) + ' %' : '—' }}
        </template>
        <template #item.last_ts="{ item }">
          {{ msToTime(new Date().getTime() - item.last_ts) }}
        </template>
      </v-data-table>
      <v-data-table
        v-else
        v-model="selectedIds" :items="assets" item-value="id" show-select
        :headers="[{ title: 'Nom', key: 'label' }]"
        :sort-by="[{ key: 'label', order: 'asc' }]" class="elevation-1" :loading="isLoading"
      />
    </v-col>
    <v-col cols="5">
      <v-row>
        <v-col cols="6">
          <v-text-field v-model="startDate" label="Début" type="date" />
        </v-col>
        <v-col cols="6">
          <v-text-field v-model="endDate" label="Fin" type="date" />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6">
          <v-checkbox v-model="aggEnabled" label="Aggrégation" />
        </v-col>
        <v-col v-if="aggEnabled" cols="6">
          <v-text-field v-model.number="aggHours" label="Nombre d'heures d'aggrégation" type="number" :min="2" :max="168" />
        </v-col>
      </v-row>
      <v-row v-if="viewMode === 'assets' && aggEnabled">
        <v-col>
          <v-radio-group v-model="assetAggType" inline hide-details label="Aggrégation">
            <v-radio label="Moyenne" value="AVG" />
            <v-radio label="Minimum" value="MIN" />
            <v-radio label="Maximum" value="MAX" />
          </v-radio-group>
        </v-col>
      </v-row>
      <v-btn color="primary" :loading="isFetching" :disabled="selectedIds.length === 0" @click="viewMode === 'devices' ? fetchData() : fetchAssetData()">
        Récupérer les données
      </v-btn>
    </v-col>
  </v-row>
  <v-row v-for="(chart, i) in charts" v-show="viewMode === 'devices'" :key="'d'+i" class="ma-1">
    <v-col cols="6">
      <v-card>
        <v-card-title class="d-flex align-center">
          {{ chart.name }}
          <v-spacer />
          <v-btn size="small" prepend-icon="mdi-download" @click="exportCsv(chart)">Exporter CSV</v-btn>
        </v-card-title>
        <v-data-table
          :headers="aggEnabled
            ? [
                { title: 'Date', key: 'date' },
                { title: 'Temp moy. (°C)', key: 'temp' },
                { title: 'Temp min. (°C)', key: 'tempMin' },
                { title: 'Temp max. (°C)', key: 'tempMax' },
                ...(chart.hasWater ? [
                  { title: 'Humid. moy. (%)', key: 'water' },
                  { title: 'Humid. min. (%)', key: 'waterMin' },
                  { title: 'Humid. max. (%)', key: 'waterMax' },
                ] : []),
              ]
            : [
                { title: 'Date', key: 'date' },
                { title: 'Temp (°C)', key: 'temp' },
                ...(chart.hasWater ? [{ title: 'Humidité (%)', key: 'water' }] : []),
              ]"
          :items="chart.tableData"
          density="compact"
          :items-per-page="10"
          class="elevation-1"
        >
          <template #item.temp="{ item }">
            {{ item.temp != null ? item.temp.toFixed(2) : '—' }}
          </template>
          <template #item.tempMin="{ item }">
            {{ item.tempMin != null ? item.tempMin.toFixed(2) : '—' }}
          </template>
          <template #item.tempMax="{ item }">
            {{ item.tempMax != null ? item.tempMax.toFixed(2) : '—' }}
          </template>
          <template #item.water="{ item }">
            {{ item.water != null ? item.water.toFixed(2) : '—' }}
          </template>
          <template #item.waterMin="{ item }">
            {{ item.waterMin != null ? item.waterMin.toFixed(2) : '—' }}
          </template>
          <template #item.waterMax="{ item }">
            {{ item.waterMax != null ? item.waterMax.toFixed(2) : '—' }}
          </template>
        </v-data-table>
      </v-card>
    </v-col>
    <v-col cols="6">
      <v-card>
        <v-card-title>{{ chart.name }}</v-card-title>
        <v-chart style="min-height: 350px;" :option="chart.option" autoresize />
      </v-card>
    </v-col>
  </v-row>
  <v-row v-for="(chart, i) in assetCharts" v-show="viewMode === 'assets'" :key="'a'+i" class="ma-1">
    <v-col cols="6">
      <v-card>
        <v-card-title class="d-flex align-center">
          {{ chart.name }}
          <v-spacer />
          <v-btn size="small" prepend-icon="mdi-download" @click="exportAssetCsv(chart)">Exporter CSV</v-btn>
        </v-card-title>
        <v-data-table
          :headers="chart.tableHeaders"
          :items="chart.tableData"
          density="compact"
          :items-per-page="10"
          class="elevation-1"
        />
      </v-card>
    </v-col>
    <v-col cols="6">
      <v-card>
        <v-card-title>{{ chart.name }}</v-card-title>
        <v-chart style="min-height: 350px;" :option="chart.option" autoresize />
      </v-card>
    </v-col>
  </v-row>
  <v-footer app>
    <v-spacer/>
    <v-defaults-provider :defaults="{ VBtn: { variant: 'text', size: 'x-small' } }">
      <AppNotification />
      <ButtonSettings />
    </v-defaults-provider>
  </v-footer>
</template>
